import ks from "./kBrowserShared";

// kStyles - class rules (kStyles.Add / AddStyle + Save) and named stylesheets (kStyles.AddStyles).
//
// TWO PATHS, CHOSEN BY ONE STATIC (2026-10-14):
//   - UseAdoptedSheets === false (the default, every existing product): exactly as before - one
//     <style id="kStyleSheet"> element for the class rules, one <style id=Name> per AddStyles call.
//   - UseAdoptedSheets === true (opt-in, for a host whose Content-Security-Policy has no
//     style-src 'unsafe-inline', e.g. WACommander): NO <style> element is ever created. The class
//     rules go into one constructed CSSStyleSheet and each AddStyles name into its own, all attached
//     through document.adoptedStyleSheets (and to every root passed to AdoptInto). A CSP governs
//     <style> elements and style= markup, not constructed sheets.
// Set the switch BEFORE the first rule is saved: rules already saved stay where they were put.
export class kStyles{
    public static NameSpace:string = "kStyleSheet";
    /** Opt-in: put every rule into constructed sheets on document.adoptedStyleSheets instead of <style> elements. */
    public static UseAdoptedSheets:boolean = false;
    // Created on first use, so a host on the adopted path never creates a <style> element at all.
    private static _StyleElement:HTMLStyleElement|null = null;
    public static get StyleElement():HTMLStyleElement {
        if (kStyles._StyleElement === null) {
            kStyles._StyleElement = document.createElement("style");
        }
        return kStyles._StyleElement;
    }
    public static set StyleElement(Value:HTMLStyleElement) {
        kStyles._StyleElement = Value;
    }
    public static Styles:kStyleClass[] = [];
    // The adopted path's sheets: the class rules, and one per AddStyles name.
    private static _RuleSheet:CSSStyleSheet|null = null;
    private static _NamedSheets:{ [Name:string]: CSSStyleSheet } = {};
    // Extra roots (a ShadowRoot, another document) that receive the adopted sheets too.
    private static _Roots:(Document|ShadowRoot)[] = [];
    public static AddStyle = (Title:string ): kStyleClass => {
        // DOCUMENTED EXCEPTION: pure one-line delegate; _AddNew carries the guard, try/catch and handler.
        return kStyles._AddNew(Title, (NewkStyle) => { NewkStyle.Title = Title; }, "kStyles.AddStyle");
    };
    /** The two find-or-create style adds in one shell: return the existing style, else create, init and push.
        Label is the caller's own name in a KGlobalErrorHandler report, so each adder keeps its exact error label. */
    private static _AddNew = (Title: string, Init: (NewkStyle: kStyleClass) => void, Label: string): kStyleClass => {
        // ERROR-SUPPRESSED-JUSTIFIED: shared helper - the catch reports under the CALLER's label (Label).
        if (ks.KErrorMode === true) { return new kStyleClass(); }
        try {
            const ExistingStyle:kStyleClass|null = kStyles.GetSTyleByTitle(Title);
            if(ExistingStyle){
                return ExistingStyle;
            }
            const NewkStyle:kStyleClass = new kStyleClass();
            Init(NewkStyle);
            kStyles.Styles.push(NewkStyle);
            return NewkStyle;
        } catch (e) {
            ks.KGlobalErrorHandler(e, Label);
            return new kStyleClass();
        }
    };
    public static GetSTyleByTitle = (Title:string ): kStyleClass |null=> {
        if (ks.KErrorMode === true) { return null; }
        try {
            let ExistingStyle:kStyleClass;
            for(ExistingStyle of kStyles.Styles){
                if(ExistingStyle.Title === Title){
                    return ExistingStyle;
                }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kStyles.GetSTyleByTitle");
            return null;
        }
    };
    public static Save = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrStyle:kStyleClass;
            const Sheet:CSSStyleSheet|null = kStyles._SheetForRules();
            if(Sheet){
                for(CurrStyle of kStyles.Styles){
                    if(CurrStyle.Saved === false){
                        let selector:string = "." + CurrStyle.Title;
                        if(CurrStyle.Hover){
                            selector = selector + ":hover";
                        }
                        if(CurrStyle.Active){
                            selector = selector + ":active";
                        }
                        const ruleText:string = `${selector} { ${CurrStyle.Style.cssText} }`;
                        const index:number = Sheet.insertRule(ruleText,Sheet.cssRules.length);
                        const NewRule:CSSRule|null = Sheet.cssRules[index];
                        if(NewRule && (NewRule instanceof CSSStyleRule)){
                            CurrStyle.Style = NewRule.style;
                        }
                        CurrStyle.Saved = true;
                        CurrStyle.New = false;
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kStyles.Save");
        }
    };
    // The sheet the class rules go into: the <style id=NameSpace> element's (attached on first use,
    // as before), or on the adopted path the one constructed rule sheet.
    private static _SheetForRules = (): CSSStyleSheet|null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            if (kStyles.UseAdoptedSheets === true) {
                if (kStyles._RuleSheet === null) {
                    kStyles._RuleSheet = new CSSStyleSheet();
                    kStyles._Adopt(kStyles._RuleSheet);
                    window.KDebug.kStyles = kStyles;
                }
                return kStyles._RuleSheet;
            }
            if(kStyles.StyleElement.id!== kStyles.NameSpace){
                kStyles.StyleElement.id = kStyles.NameSpace;
                document.head.appendChild(kStyles.StyleElement);
                window.KDebug.kStyles = kStyles;
            }
            return (kStyles.StyleElement.sheet as CSSStyleSheet);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kStyles._SheetForRules");
            return null;
        }
    };
    // Attach one constructed sheet to the document and every root given to AdoptInto.
    private static _Adopt = (Sheet:CSSStyleSheet): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            kStyles._AdoptOn(document, Sheet);
            for (const Root of kStyles._Roots) {
                kStyles._AdoptOn(Root, Sheet);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kStyles._Adopt");
        }
    };
    private static _AdoptOn = (Root:Document|ShadowRoot, Sheet:CSSStyleSheet): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Root.adoptedStyleSheets.indexOf(Sheet) !== -1) { return; }
            Root.adoptedStyleSheets = Root.adoptedStyleSheets.concat([Sheet]);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kStyles._AdoptOn");
        }
    };
    /** Adopted path only: also attach every sheet (now and later) to Root - a ShadowRoot or another
        document whose content uses these classes. Does nothing on the <style> path. */
    public static AdoptInto = (Root:Document|ShadowRoot): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kStyles._Roots.indexOf(Root) === -1) {
                kStyles._Roots.push(Root);
            }
            if (kStyles._RuleSheet !== null) {
                kStyles._AdoptOn(Root, kStyles._RuleSheet);
            }
            for (const Name of Object.keys(kStyles._NamedSheets)) {
                kStyles._AdoptOn(Root, kStyles._NamedSheets[Name]);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kStyles.AdoptInto");
        }
    };
    public static Add = (ClassName:string,CssText:string,Hover:boolean,Active?:boolean):kStyleClass => {
        // DOCUMENTED EXCEPTION: pure delegate; _AddNew carries the guard, try/catch and handler.
        return kStyles._AddNew(ClassName, (NewClass) => {
            NewClass.Hover = Hover;
            NewClass.Active = ks.Nb(Active);
            NewClass.Title = ClassName;
            NewClass.Style.cssText = CssText;
        }, "kStyles.Add");
    }

    /** A named stylesheet, added once: a <style id=StyleName> element, or on the adopted path a
        constructed sheet of that name. */
    public static AddStyles = (StyleName:string, Stylestring:string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (kStyles.UseAdoptedSheets === true) {
                if (kStyles._NamedSheets[StyleName] !== undefined) { return; }
                const Sheet:CSSStyleSheet = new CSSStyleSheet();
                Sheet.replaceSync(Stylestring);
                kStyles._NamedSheets[StyleName] = Sheet;
                kStyles._Adopt(Sheet);
                return;
            }
            const ExistingStyle: HTMLElement | null = document.getElementById(StyleName);
            if (ExistingStyle === null) {
                const Style: HTMLStyleElement = document.createElement('style');
                Style.id = StyleName;
                Style.innerHTML = Stylestring;
                document.head.appendChild(Style);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kStyles.AddStyles");
        }

    };

}

export class kStyleClass {
    public Title:string = "";
    public Saved:boolean = false;
    public New:boolean = true;
    public Hover:boolean = false;
    public Active:boolean = false;
    private _FakeElem:HTMLSpanElement = document.createElement("span");
    public Style:CSSStyleDeclaration =this._FakeElem.style;
    public Apply = (Obj:HTMLElement): void => {
        // DOCUMENTED EXCEPTION: pure one-line delegate; _SetOn carries the guard, try/catch and handler.
        this._SetOn(Obj, true, "kStyleClass.Apply");
    };
    /** The two class toggles in one shell: add or remove this.Title when not already in that state, then Save once. */
    private _SetOn = (Obj:HTMLElement, On:boolean, Label:string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: shared helper - the catch reports under the CALLER's label (Label).
        if (ks.KErrorMode === true) return;
        try {
            if(Obj.classList.contains(this.Title) !== On){
                if(On === true){
                    Obj.classList.add(this.Title);
                }else{
                    Obj.classList.remove(this.Title);
                }
            }
            if(this.Saved === false){
                kStyles.Save();
            }
        } catch (e) {ks.KGlobalErrorHandler(e, Label);}
    };  
    public RemoveClassName = (Obj:HTMLElement): void => {
        // DOCUMENTED EXCEPTION: pure one-line delegate; _SetOn carries the guard, try/catch and handler.
        this._SetOn(Obj, false, "kStyleClass.RemoveClassName");
    };  
    
}
